import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User, Hash, ArrowRight } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { usePlayerAuth } from '../contexts/PlayerAuthContext';
import { useIsCoach } from '../hooks/useIsCoach';
import { cn } from '../lib/utils';
import { findEventsNavItem } from '../lib/eventsPage';
import { CLUB_APP_PREFIX } from '../lib/tenantPath';
import { isPublicTenantPath, isWithinAccessScope } from '../lib/tenantAccess';
import { BRAND_SANS } from '../lib/editorialTheme';
import { hasClubChrome, CLUB_HEADER_HEIGHT } from '../lib/tenantChrome';
import LanguageFlagPicker from './LanguageFlagPicker';

// Tenant sites: only show the branded header on a small set of public-facing pages.
const TENANT_HEADER_ALLOWED_PATHS = new Set<string>([
    '/', // tenant landing
    '/public/tournaments',
    '/public/open',
    '/discover',
    '/org/join',
    '/join',
    '/player/home',
    '/coach/home',
    '/player/calendar',
    '/player/settings',
    '/player/login',
    '/opening-tree',
    '/observe-game',
    '/play-chess',
    '/drawback-chess',
    '/drawback-chess/glossary',
    '/chess-roulette',
    '/chess-roulette/glossary',
    '/chess960',
    '/sign-in',
    '/analysis',
]);

interface TenantHeaderProps {
    organizationName?: string;
    logoUrl?: string;
    showSearch?: boolean;
    transparent?: boolean;
    onRefresh?: () => void;
    isRefreshing?: boolean;
}

/**
 * TenantHeader - Consistent header component for all tenant pages.
 * Translucent, blurred bar with pill navigation; brand on the left,
 * event-code entry and account on the right.
 */
const TenantHeader: React.FC<TenantHeaderProps> = ({
    organizationName,
    logoUrl,
}) => {
    const { tenant, tenantPath, stripTenantPrefix, shadowPrefix, accent, editorial } = useTenant();
    const { isLoggedIn, player } = usePlayerAuth();
    const { coachClubs } = useIsCoach();
    const location = useLocation();
    const navigate = useNavigate();
    const [tournamentCode, setTournamentCode] = useState('');

    const handleCodeSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const code = tournamentCode.trim().toUpperCase();
        if (code) {
            navigate(`${tenantPath('/public/open')}?code=${encodeURIComponent(code)}`);
            setTournamentCode('');
        }
    };

    if (!tenant) {
        return null;
    }

    const normalizedPathname =
        location.pathname.length > 1 ? location.pathname.replace(/\/+$/, '') : location.pathname;
    const logicalPath = stripTenantPrefix(normalizedPathname);
    const isTenantWebsitePage = logicalPath.startsWith('/site/');
    const isAllowedPath =
        TENANT_HEADER_ALLOWED_PATHS.has(logicalPath) ||
        logicalPath.startsWith('/public/tournament/') ||
        logicalPath.startsWith('/public/tournaments/') ||
        logicalPath.startsWith('/analysis/') ||
        logicalPath.startsWith('/boards/') ||
        // A club form is one of its public pages, like an event — the form
        // page renders this header, and without it opens on a bare band.
        logicalPath.startsWith('/forms/') ||
        isTenantWebsitePage;
    if (!isAllowedPath) {
        return null;
    }

    const orgName = organizationName || tenant?.name || 'Organization';
    const orgLogo = logoUrl || tenant?.branding?.logoUrl;

    /**
     * The brand goes home — and on a host whose root is the club's own site,
     * home is that site rather than the tournament hub we are sitting in.
     */
    const homeHref = shadowPrefix === CLUB_APP_PREFIX ? '/' : tenantPath('/');

    // Editorial swaps the pill nav for plain letterspaced links and drops the
    // club colour; `edAccent` is what every accent override reads instead.
    const ed = editorial;
    const edAccent = ed ? null : accent;

    const isActive = (to: string) =>
        location.pathname === to || location.pathname.startsWith(`${to}/`);

    // A club with its own Events page gets one events entry, not two: that page
    // lists the club nights and the regular week as well as the tournaments,
    // so the built-in tournaments link would be a second, thinner answer. When
    // the page is published but kept out of the menu, the entry stays and
    // points at it — never back at /public/tournaments.
    const ownEventsPage = findEventsNavItem(tenant?.websiteNavItems);
    const eventsHref = tenant?.eventsPageHref || tenantPath('/public/tournaments');

    const allNavLinks = [
        ...(ownEventsPage ? [] : [{ to: eventsHref, label: 'Events', external: false }]),
        ...(tenant?.websiteNavItems || []).map((item) => ({
            to: item.external ? item.href : tenantPath(item.href),
            label: item.label,
            external: item.external,
        })),
        { to: tenantPath('/player/home'), label: 'My Dashboard', external: false },
        ...(isLoggedIn && coachClubs.some((c) => c.organizationId === tenant.id)
            ? [{ to: tenantPath('/coach/home'), label: 'Coaching', external: false }]
            : []),
        ...(tenant?.customNavLinks || []).map((l) => ({
            to: /^https?:\/\//i.test((l.url || '').trim()) ? l.url : tenantPath(l.url),
            label: l.label,
            external: /^https?:\/\//i.test((l.url || '').trim()),
        })),
    ];

    // Members-only club: a signed-out visitor only gets the links that will
    // open for them — everything else would bounce straight to the login.
    const membersOnly =
        Boolean(tenant?.access?.requireSignIn) &&
        !isLoggedIn &&
        isWithinAccessScope(location.pathname, tenant?.access?.scopePrefix);
    const navLinks = membersOnly
        ? allNavLinks.filter(
              (link) =>
                  !link.external &&
                  isPublicTenantPath(stripTenantPrefix(link.to), tenant?.access?.publicPaths)
          )
        : allNavLinks;

    /**
     * A club with its own site gets that site's nav here, so the two halves of
     * the domain read as one: the club's hand-built pages at `/` and the app
     * under `/events`. Everything below mirrors `.mainnav` in
     * client/public/club-sites/<key>/css/site.css — same height, same 26px
     * crest, same small-caps type, links right-aligned — so keep them in step.
     *
     * The platform's own furniture goes: no language picker (the club's site
     * has none, and its pages are English), and the account is a nav link in
     * the same type as the rest rather than a dark avatar chip that belongs to
     * a different design.
     */
    if (hasClubChrome(tenant)) {
        const firstName = player?.name?.split(' ')[0] || 'Account';
        // Hexes are the club site's own tokens (--ink, --faint, --line in
        // css/site.css): warm, where Tailwind's greys are cool, which is most
        // of what made the two halves of the domain look unrelated.
        const navLinkClass = (active: boolean) =>
            cn(
                'pb-0.5 border-b text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors whitespace-nowrap',
                active
                    ? 'text-[#17140f] border-[#17140f]'
                    : 'text-[#7a6f63] border-transparent hover:text-[#17140f]'
            );

        return (
            <header
                className="sticky top-0 z-50 bg-white/[0.92] backdrop-blur-xl border-b border-[#e8e3da] w-full safe-top"
                style={{ fontFamily: BRAND_SANS.fontFamily }}
            >
                {/* The app's own page gutter — `pageGutterClass` in
                    pages/PlayerHomePage.tsx — so the brand sits on the same
                    left edge as the page under it. The club site's nav uses a
                    narrower measure (--max: 58rem); matching the app is what
                    keeps the header from floating in over its own content. */}
                <div
                    className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-4"
                    style={{ minHeight: CLUB_HEADER_HEIGHT }}
                >
                    {/* Crest and wordmark, as on the club's own pages */}
                    <Link to={homeHref} className="flex items-center gap-2.5 group min-w-0 shrink">
                        {orgLogo ? (
                            <img
                                src={orgLogo}
                                alt={orgName}
                                className="h-[26px] w-[26px] object-cover flex-shrink-0"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                        ) : (
                            <div className="h-[26px] w-[26px] bg-[#17140f] flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-bold text-[11px]">{orgName[0]?.toUpperCase()}</span>
                            </div>
                        )}
                        <span
                            title={orgName}
                            className="text-[12px] font-bold uppercase tracking-[0.22em] text-[#17140f] truncate max-w-[45vw] sm:max-w-[16rem] group-hover:text-[#736b63] transition-colors"
                        >
                            {orgName}
                        </span>
                    </Link>

                    {/* Links sit to the right of the wordmark, as they do on the club site */}
                    <nav className="hidden md:flex items-center gap-6 ml-auto min-w-0 overflow-x-auto">
                        {navLinks.map(({ to, label, external }) =>
                            external ? (
                                <a
                                    key={`${label}-${to}`}
                                    href={to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={navLinkClass(false)}
                                >
                                    {label}
                                </a>
                            ) : (
                                <Link key={`${label}-${to}`} to={to} className={navLinkClass(isActive(to))}>
                                    {label}
                                </Link>
                            )
                        )}
                    </nav>

                    <div className="flex items-center gap-5 ml-auto md:ml-6 flex-shrink-0">
                        {/* Event code — a hairline field, not a pill */}
                        <form onSubmit={handleCodeSubmit} className="hidden lg:flex items-center">
                            <div className="flex items-center border border-[#e8e3da] transition-colors focus-within:border-[#17140f]">
                                <input
                                    type="text"
                                    value={tournamentCode}
                                    onChange={e => setTournamentCode(e.target.value)}
                                    placeholder="Event code"
                                    aria-label="Event code"
                                    className="w-24 bg-transparent px-2.5 py-[7px] text-[11px] tracking-[0.06em] text-[#17140f] placeholder:text-[#7a6f63] focus:outline-none"
                                    maxLength={20}
                                />
                                <button
                                    type="submit"
                                    disabled={!tournamentCode.trim()}
                                    aria-label="Open event by code"
                                    className={cn(
                                        'px-2 py-[7px] transition-colors',
                                        tournamentCode.trim()
                                            ? 'text-[#17140f] hover:text-black'
                                            : 'text-[#d6cfc4]'
                                    )}
                                >
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </form>

                        {isLoggedIn && player ? (
                            /* The account reads as the last link in the row: the player's
                               name in the nav's own type, with their picture — or their
                               initial in a hairline square — rather than a filled chip. */
                            <Link
                                to={tenantPath('/player/settings')}
                                title={`${player.name || firstName} — account`}
                                className="flex items-center gap-2 group"
                            >
                                {player.avatarUrl ? (
                                    <img
                                        src={player.avatarUrl}
                                        alt=""
                                        className="h-[26px] w-[26px] object-cover flex-shrink-0"
                                    />
                                ) : (
                                    <span className="h-[26px] w-[26px] border border-[#e8e3da] flex items-center justify-center text-[10px] font-bold text-[#17140f] flex-shrink-0 transition-colors group-hover:border-[#17140f]">
                                        {firstName[0]?.toUpperCase()}
                                    </span>
                                )}
                                <span className="hidden sm:inline text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7a6f63] group-hover:text-[#17140f] transition-colors max-w-[9rem] truncate">
                                    {firstName}
                                </span>
                            </Link>
                        ) : (
                            /* The club site's own button: square, near-black, small caps */
                            <Link
                                to={tenantPath('/player/login')}
                                className="inline-flex items-center gap-1.5 border border-[#17140f] bg-[#17140f] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-black hover:border-black whitespace-nowrap"
                            >
                                <User className="w-3 h-3 flex-shrink-0" />
                                Sign In
                            </Link>
                        )}
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-gray-200/60 w-full safe-top">
            <div className="max-w-7xl mx-auto px-4 md:px-6 flex h-[48px] items-center gap-3">
                {/* Brand — name truncates so a long org name never pushes the account chip off-screen */}
                <Link to={homeHref} className="flex items-center gap-2 group min-w-0 shrink">
                    {orgLogo ? (
                        <img
                            src={orgLogo}
                            alt={orgName}
                            className={cn(
                                'object-contain bg-gray-50 border border-gray-100 p-0.5 flex-shrink-0',
                                ed ? 'h-6 w-6' : 'h-7 w-7 rounded-lg'
                            )}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                    ) : (
                        <div className={cn(
                            'bg-gradient-to-br from-gray-900 to-gray-600 flex items-center justify-center flex-shrink-0',
                            ed ? 'w-6 h-6' : 'w-7 h-7 rounded-lg shadow-sm'
                        )}>
                            <span className="text-white font-black text-xs">{orgName[0]?.toUpperCase()}</span>
                        </div>
                    )}
                    <span
                        title={orgName}
                        className={cn(
                            'text-gray-900 truncate max-w-[45vw] sm:max-w-[16rem] group-hover:text-gray-600 transition-colors',
                            ed
                                ? 'text-[12px] font-bold uppercase tracking-[0.22em]'
                                : 'text-sm font-extrabold tracking-tight'
                        )}
                        style={ed ? BRAND_SANS : undefined}
                    >
                        {orgName}
                    </span>
                </Link>

                {/* Pill navigation */}
                <nav className={cn(
                    'hidden md:flex items-center min-w-0 overflow-x-auto',
                    ed ? 'gap-6 ml-8' : 'gap-1 ml-4'
                )}>
                    {navLinks.map(({ to, label, external }) =>
                        external ? (
                            <a
                                key={`${label}-${to}`}
                                href={to}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cn(
                                    'transition-colors whitespace-nowrap text-gray-500 hover:text-gray-900',
                                    ed
                                        ? 'text-[10px] font-semibold uppercase tracking-[0.18em]'
                                        : 'px-3 py-1 rounded-full text-[13px] font-semibold hover:bg-gray-100'
                                )}
                            >
                                {label}
                            </a>
                        ) : (
                            <Link
                                key={`${label}-${to}`}
                                to={to}
                                className={cn(
                                    'transition-colors whitespace-nowrap',
                                    ed
                                        ? 'text-[10px] font-semibold uppercase tracking-[0.18em] pb-0.5 border-b'
                                        : 'px-3 py-1 rounded-full text-[13px] font-semibold',
                                    isActive(to)
                                        ? ed
                                            ? 'text-gray-900 border-gray-900'
                                            : 'bg-gray-900 text-white shadow-sm'
                                        : ed
                                            ? 'text-gray-400 border-transparent hover:text-gray-900'
                                            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                                )}
                                style={isActive(to) && edAccent ? { background: edAccent } : undefined}
                            >
                                {label}
                            </Link>
                        )
                    )}
                </nav>

                <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                    <LanguageFlagPicker compact />

                    {/* Event code entry */}
                    <form onSubmit={handleCodeSubmit} className="hidden lg:flex items-center">
                        <div className={cn(
                            'flex items-center gap-1 pl-2.5 pr-1 py-0.5 transition-all focus-within:bg-white focus-within:ring-1 focus-within:ring-gray-300',
                            ed ? 'border border-gray-200' : 'bg-gray-100/80 rounded-full'
                        )}>
                            <Hash className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <input
                                type="text"
                                value={tournamentCode}
                                onChange={e => setTournamentCode(e.target.value)}
                                placeholder="Event code"
                                className="px-1 py-0.5 text-[13px] text-gray-800 placeholder:text-gray-400 focus:outline-none bg-transparent w-24"
                                maxLength={20}
                            />
                            <button
                                type="submit"
                                disabled={!tournamentCode.trim()}
                                aria-label="Open event by code"
                                className={cn(
                                    'w-[22px] h-[22px] flex items-center justify-center transition-colors',
                                    !ed && 'rounded-full',
                                    tournamentCode.trim()
                                        ? 'bg-gray-900 text-white hover:bg-black'
                                        : 'bg-gray-200/70 text-gray-400'
                                )}
                                style={tournamentCode.trim() && edAccent ? { background: edAccent } : undefined}
                            >
                                <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </form>

                    {/* Account — always show Sign In / profile in the top header */}
                    <div className="flex items-center">
                        {isLoggedIn && player ? (
                            <Link
                                to={tenantPath('/player/settings')}
                                className={cn(
                                    'flex items-center gap-1.5 pl-0.5 pr-2 py-0.5 hover:bg-gray-100 transition-colors text-[12px] font-semibold text-gray-700',
                                    !ed && 'rounded-full'
                                )}
                            >
                                <div
                                    className={cn(
                                        'w-7 h-7 bg-gradient-to-br from-gray-900 to-gray-600 flex items-center justify-center text-[11px] font-bold text-white shadow-sm overflow-hidden flex-shrink-0',
                                        ed ? 'rounded-none' : 'rounded-full'
                                    )}
                                    style={edAccent && !player.avatarUrl ? { background: edAccent } : undefined}
                                >
                                    {player.avatarUrl
                                        ? <img src={player.avatarUrl} alt={player.name} className="w-full h-full object-cover" />
                                        : player.name?.charAt(0) || 'P'}
                                </div>
                                <span className="hidden sm:inline">{player.name?.split(' ')[0] || 'Profile'}</span>
                            </Link>
                        ) : (
                            <Link
                                to={tenantPath('/player/login')}
                                className={cn(
                                    'flex items-center gap-1 bg-gray-900 text-white hover:bg-black transition-colors whitespace-nowrap',
                                    ed ? 'h-6' : 'h-7',
                                    ed
                                        ? 'px-3 text-[9px] font-bold uppercase tracking-[0.18em]'
                                        : 'px-2.5 rounded-full font-semibold text-[12px] shadow-sm'
                                )}
                                style={edAccent ? { background: edAccent } : undefined}
                            >
                                <User className="w-3 h-3 flex-shrink-0" />
                                Sign In
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default TenantHeader;
